/* eslint-disable func-names */
/* eslint-disable no-shadow */
/* eslint-disable no-undef */
/* eslint-disable no-console */
import React, { useState, useRef, useEffect } from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator, Platform,
} from 'react-native';
import { WebView } from 'react-native-webview';
import { Asset } from 'expo-asset';
import * as FileSystem from 'expo-file-system';
import { useNavigation } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import Colors from '../constants/Colors';
import PressableStyled from './PressableStyled';
import acceptTerms from '../requests/Users/AcceptTerms';
import { updateTermsAccepted } from '../redux/userSlice';

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    pdfjsLib: any;
    // eslint-disable-next-line no-unused-vars
    pdfViewerLog?: (message: string, type?: string) => void;
  }
}

interface SimplePDFViewerProps {
  url?: string;
  onEndReached?: () => void;
  dismiss?: () => void;
}

export default function SimplePDFViewer({
  url, onEndReached, dismiss,
}: SimplePDFViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [tickedAccepted, setTickedAccepted] = useState(false);
  const [pdfUri, setPdfUri] = useState<string | null>(null);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const webViewRef = useRef<WebView>(null);
  const isWeb = Platform.OS === 'web';
  const webDivRef = isWeb ? useRef<HTMLDivElement>(null) : null;
  const navigation = useNavigation();
  const dispatch = useDispatch();

  useEffect(() => {
    async function loadPdf() {
      try {
        setLoading(true);
        setError(null);

        let uri: string;
        try {
          // const asset = Asset.fromURI('https://pdf-lib.js.org/assets/us_constitution.pdf');
          const asset = Asset.fromURI('https://s3.eu-west-1.amazonaws.com/hereworks.tech/website_root/docs/Happy-SaaS-TCs.pdf');
          await asset.downloadAsync();
          uri = asset.localUri || (asset as any).uri;
        } catch (assetErr) {
          console.error('Error loading asset, trying direct file path:', assetErr);
          throw assetErr;
        }

        if (!uri) {
          throw new Error('Failed to resolve PDF URI');
        }

        console.log('PDF URI resolved to:', uri);
        setPdfUri(uri);

        if (!isWeb) {
          try {
            const b64 = await new FileSystem.File(uri).base64();
            setPdfBase64(`data:application/pdf;base64,${b64}`);
          } catch (b64Err) {
            console.error('Failed to read PDF as base64:', b64Err);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(`Failed to load PDF: ${err instanceof Error ? err.message : String(err)}`);
        setLoading(false);
      }
    }

    loadPdf();
  }, [url, isWeb]);

  useEffect(() => {
    if (hasReachedEnd && onEndReached) {
      console.log('Triggering onEndReached callback');
      onEndReached();
    }
  }, [hasReachedEnd, onEndReached]);

  const fetchPDFAsBlob = async (pdfUrl: string): Promise<string> => {
    console.log('Fetching PDF as blob from URL:', pdfUrl);
    try {
      const urlWithTimestamp = pdfUrl.includes('?')
        ? `${pdfUrl}&t=${Date.now()}`
        : `${pdfUrl}?t=${Date.now()}`;

      const response = await fetch(urlWithTimestamp, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching PDF as blob:', error);
      throw new Error(`Failed to fetch PDF: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  useEffect(() => {
    if (!isWeb || !pdfUri || !webDivRef?.current) return;

    console.log('Initializing PDF.js viewer for web with URI:', pdfUri);

    const containerDiv = webDivRef.current;

    const loadPdfJs = async () => {
      let finalPdfUrl = pdfUri;

      try {
        if (pdfUri.startsWith('http')) {
          finalPdfUrl = await fetchPDFAsBlob(pdfUri);
          console.log('Converted PDF URL to blob URL:', finalPdfUrl);
        } else if (pdfUri.startsWith('/')) {
          finalPdfUrl = `${window.location.origin}${pdfUri}`;
          console.log('Converted relative path to absolute URL:', finalPdfUrl);
        }
      } catch (urlErr) {
        console.error('Failed to pre-process URL:', urlErr);
      }

      if (window.pdfjsLib) {
        initializeViewer(finalPdfUrl);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.7.107/build/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.7.107/build/pdf.worker.min.js';
        initializeViewer(finalPdfUrl);
      };
      script.onerror = (err) => {
        console.error('Failed to load PDF.js library:', err);
        setError('Failed to load PDF viewer library. Please check your internet connection.');
      };
      document.head.appendChild(script);
    };

    const initializeViewer = (pdfUrl: string) => {
      containerDiv.innerHTML = '';

      const viewerContainer = document.createElement('div');
      viewerContainer.style.position = 'absolute';
      viewerContainer.style.top = '0';
      viewerContainer.style.right = '0';
      viewerContainer.style.bottom = '40px';
      viewerContainer.style.left = '0';
      viewerContainer.style.overflow = 'auto';
      viewerContainer.style.background = '#eee';
      viewerContainer.style.setProperty('-webkit-overflow-scrolling', 'touch');

      const viewer = document.createElement('div');
      viewer.id = 'pdf-viewer';
      viewer.style.width = '100%';
      viewerContainer.appendChild(viewer);

      const progressContainer = document.createElement('div');
      progressContainer.style.position = 'absolute';
      progressContainer.style.bottom = '0px';
      progressContainer.style.left = '10px';
      progressContainer.style.right = '10px';
      progressContainer.style.height = '10px';
      progressContainer.style.background = '#e0e0e0';
      progressContainer.style.borderRadius = '5px';
      progressContainer.style.overflow = 'hidden';
      progressContainer.style.zIndex = '1000';

      const progressBar = document.createElement('div');
      progressBar.style.height = '100%';
      progressBar.style.background = '#4285f4';
      progressBar.style.width = '0';
      progressBar.style.transition = 'width 0.3s';
      progressContainer.appendChild(progressBar);

      containerDiv.appendChild(viewerContainer);
      containerDiv.appendChild(progressContainer);

      if (process.env.NODE_ENV === 'development') {
        const debugBtn = document.createElement('button');
        debugBtn.innerText = 'Toggle Debug';
        debugBtn.style.position = 'fixed';
        debugBtn.style.bottom = '10px';
        debugBtn.style.left = '10px';
        debugBtn.style.zIndex = '9999';
        debugBtn.style.padding = '8px 16px';
        debugBtn.style.backgroundColor = '#007bff';
        debugBtn.style.color = 'white';
        debugBtn.style.border = 'none';
        debugBtn.style.borderRadius = '4px';
        debugBtn.style.cursor = 'pointer';
        containerDiv.appendChild(debugBtn);

        const debugConsole = document.createElement('div');
        debugConsole.style.position = 'fixed';
        debugConsole.style.top = '0';
        debugConsole.style.left = '0';
        debugConsole.style.right = '0';
        debugConsole.style.backgroundColor = 'rgba(0,0,0,0.8)';
        debugConsole.style.color = '#00ff00';
        debugConsole.style.padding = '10px';
        debugConsole.style.fontSize = '12px';
        debugConsole.style.fontFamily = 'monospace';
        debugConsole.style.maxHeight = '200px';
        debugConsole.style.overflow = 'auto';
        debugConsole.style.zIndex = '9999';
        debugConsole.style.display = 'none';
        containerDiv.appendChild(debugConsole);

        debugBtn.onclick = function () {
          debugConsole.style.display = debugConsole.style.display === 'none' ? 'block' : 'none';
        };

        const logToConsole = (message: string, type = 'info') => {
          const logEntry = document.createElement('div');
          let color;
          if (type === 'error') {
            color = '#ff5252';
          } else if (type === 'success') {
            color = '#4caf50';
          } else {
            color = '#00ff00';
          }
          logEntry.innerHTML = `<span style="color:#888;">[${new Date().toISOString().substring(11, 23)}]</span> <span style="color:${color};">${message}</span>`;
          debugConsole.appendChild(logEntry);
          debugConsole.scrollTop = debugConsole.scrollHeight;
          console.log(`[PDF Viewer] ${message}`);
        };

        window.pdfViewerLog = logToConsole;

        logToConsole(`Loading PDF from: ${pdfUrl}`, 'info');
      }

      const retryBtn = document.createElement('button');
      retryBtn.innerText = 'Retry Loading PDF';
      retryBtn.style.position = 'absolute';
      retryBtn.style.top = '50%';
      retryBtn.style.left = '50%';
      retryBtn.style.transform = 'translate(-50%, -50%)';
      retryBtn.style.zIndex = '9999';
      retryBtn.style.padding = '12px 24px';
      retryBtn.style.backgroundColor = '#007bff';
      retryBtn.style.color = 'white';
      retryBtn.style.border = 'none';
      retryBtn.style.borderRadius = '4px';
      retryBtn.style.cursor = 'pointer';
      retryBtn.style.display = 'none';
      retryBtn.onclick = function () {
        loadPdfDocument();
      };
      containerDiv.appendChild(retryBtn);

      const errorDisplayContainer = document.createElement('div');
      errorDisplayContainer.style.position = 'absolute';
      errorDisplayContainer.style.top = '50%';
      errorDisplayContainer.style.left = '50%';
      errorDisplayContainer.style.transform = 'translate(-50%, -50%)';
      errorDisplayContainer.style.padding = '20px';
      errorDisplayContainer.style.background = 'white';
      errorDisplayContainer.style.border = '1px solid #ddd';
      errorDisplayContainer.style.borderRadius = '5px';
      errorDisplayContainer.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
      errorDisplayContainer.style.maxWidth = '80%';
      errorDisplayContainer.style.display = 'none';
      errorDisplayContainer.style.zIndex = '998';
      containerDiv.appendChild(errorDisplayContainer);

      const loadPdfDocument = () => {
        try {
          retryBtn.style.display = 'none';
          errorDisplayContainer.style.display = 'none';
          setError(null);

          const downloadBtn = document.createElement('button');
          downloadBtn.innerText = 'Download PDF';
          downloadBtn.style.marginLeft = '10px';
          downloadBtn.style.padding = '12px 24px';
          downloadBtn.style.backgroundColor = '#ffc107';
          downloadBtn.style.color = 'black';
          downloadBtn.style.border = 'none';
          downloadBtn.style.borderRadius = '4px';
          downloadBtn.style.cursor = 'pointer';
          downloadBtn.onclick = function () {
            // For S3 URL, open in new tab
            if (pdfUri.includes('s3.eu-west-1.amazonaws.com')) {
              window.open(pdfUri, '_blank');
            } else {
              // For blob URL, create a download link
              const a = document.createElement('a');
              a.href = pdfUrl;
              a.download = 'Happy-SaaS-TCs.pdf';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }
          };

          const loadingTask = window.pdfjsLib.getDocument({
            url: pdfUrl,
            cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.7.107/cmaps/',
            cMapPacked: true,
            enableXfa: true, // Enable XFA form support
            withCredentials: false, // Try without credentials first
          });

          // Add loading progress
          loadingTask.onProgress = (progressData) => {
            if (progressData.total > 0) {
              const percent = (progressData.loaded / progressData.total) * 100;
              progressBar.style.width = `${percent}%`;
            }
          };

          loadingTask.promise
            .then((pdf) => {
              console.log(`PDF loaded with ${pdf.numPages} pages`);

              let pageNum = 1;
              const totalPages = pdf.numPages;
              let hasMarkedAsRead = false;
              let lastScrollPercentage = 0;

              // Reset progress bar
              progressBar.style.width = '0%';

              // Render all pages
              // eslint-disable-next-line no-plusplus
              for (let i = 1; i <= pdf.numPages; i++) {
                // eslint-disable-next-line no-loop-func
                pdf.getPage(i).then((page) => {
                  const viewport = page.getViewport({ scale: 1.0 });
                  const scale = viewerContainer.clientWidth / viewport.width;
                  const scaledViewport = page.getViewport({ scale });

                  const pageDiv = document.createElement('div');
                  pageDiv.className = 'pdf-page';
                  pageDiv.dataset.pageNumber = String(i);
                  pageDiv.style.margin = '10px auto';
                  pageDiv.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.2)';
                  pageDiv.style.background = 'white';
                  pageDiv.style.width = `${scaledViewport.width}px`;
                  pageDiv.style.height = `${scaledViewport.height}px`;

                  viewer.appendChild(pageDiv);

                  const canvas = document.createElement('canvas');
                  canvas.width = scaledViewport.width;
                  canvas.height = scaledViewport.height;

                  pageDiv.appendChild(canvas);

                  page.render({
                    canvasContext: canvas.getContext('2d'),
                    viewport: scaledViewport,
                  });

                  // Track loading progress
                  if (i === 1) {
                    // Set up scroll handler once first page is rendered
                    let isScrolling = false;
                    let scrollTimeout = null;

                    const handleScroll = () => {
                      if (scrollTimeout) {
                        clearTimeout(scrollTimeout);
                      }

                      if (!isScrolling) {
                        isScrolling = true;
                      }

                      scrollTimeout = setTimeout(() => {
                        isScrolling = false;
                        updateProgress();
                      }, 100);
                    };

                    const updateProgress = () => {
                      // Get current visible page
                      const pages = document.querySelectorAll('.pdf-page');
                      let visiblePage = 1;

                      // eslint-disable-next-line no-shadow
                      pages.forEach((page) => {
                        const rect = page.getBoundingClientRect();
                        // eslint-disable-next-line max-len
                        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
                          visiblePage = parseInt((page as HTMLElement).dataset.pageNumber || '1', 10);
                        }
                      });

                      // Update page number display
                      if (pageNum !== visiblePage) {
                        pageNum = visiblePage;
                      }

                      // Calculate scroll percentage
                      const { scrollTop } = viewerContainer;
                      const { scrollHeight } = viewerContainer;
                      const { clientHeight } = viewerContainer;
                      const maxScrollTop = scrollHeight - clientHeight;
                      const scrollPercentage = (scrollTop / maxScrollTop) * 100;

                      // Update progress bar
                      progressBar.style.width = `${Math.min(scrollPercentage, 100)}%`;

                      // Don't spam with updates at 100%
                      if (scrollPercentage > 99 && lastScrollPercentage > 99) {
                        return;
                      }

                      lastScrollPercentage = scrollPercentage;

                      // Check if reached end
                      if ((scrollPercentage >= 90 || pageNum === totalPages) && !hasMarkedAsRead) {
                        hasMarkedAsRead = true;
                        progressBar.style.background = '#4CAF50';
                        progressBar.style.width = '100%';

                        if (window.pdfViewerLog) {
                          window.pdfViewerLog('Document marked as read', 'success');
                        }

                        setHasReachedEnd(true);
                      }
                    };

                    viewerContainer.addEventListener('scroll', handleScroll, { passive: true });
                    document.addEventListener('touchend', updateProgress, { passive: true });
                    document.addEventListener('mouseup', updateProgress, { passive: true });

                    // Check after a small delay to deal with initial rendering
                    setTimeout(updateProgress, 500);
                  }
                }).catch((pageError) => {
                  console.error(`Error rendering page ${i}:`, pageError);
                  if (window.pdfViewerLog) {
                    window.pdfViewerLog(`Error rendering page ${i}: ${pageError.message}`, 'error');
                  }
                });
              }

              // Set a timeout to mark read if user hasn't scrolled
              setTimeout(() => {
                if (!hasMarkedAsRead) {
                  hasMarkedAsRead = true;
                  progressBar.style.background = '#4CAF50';
                  progressBar.style.width = '100%';

                  if (window.pdfViewerLog) {
                    window.pdfViewerLog('Document marked as read (timeout)', 'warn');
                  }

                  setHasReachedEnd(true);
                }
              }, 30000);
            })
            .catch((error) => {
              console.error('Error loading PDF:', error);
              const errorMessage = error.message || 'Unknown error';

              errorDisplayContainer.style.display = 'block';
              errorDisplayContainer.innerHTML = `
                <h3 style="margin-top:0;color:red;">Error Loading PDF</h3>
                <p><strong>Error:</strong> ${errorMessage}</p>
                ${error.stack ? `<details>
                  <summary style="cursor:pointer;color:#666;">Technical Details</summary>
                  <pre style="background:#f5f5f5;padding:10px;overflow:auto;max-height:200px;font-size:11px;">${error.stack}</pre>
                </details>` : ''}
                <p>Try one of the following options:</p>
                <div style="display:flex;justify-content:center;margin-top:15px;">
                  <button id="retry-btn" style="padding:10px 20px;background:#007bff;color:white;border:none;border-radius:4px;cursor:pointer;">Retry</button>
                </div>
              `;

              // Add the Try Local button to the error display
              const buttonContainer = errorDisplayContainer.querySelector('div');
              buttonContainer.appendChild(downloadBtn);

              // Connect the retry button
              const retryButton = errorDisplayContainer.querySelector('#retry-btn') as HTMLButtonElement | null;
              if (retryButton) {
                retryButton.onclick = function () {
                  loadPdfDocument();
                };

                if (window.pdfViewerLog) {
                  window.pdfViewerLog(`PDF load error: ${errorMessage}`, 'error');
                  window.pdfViewerLog(`Stack: ${error.stack || 'No stack trace'}`, 'error');
                }

                setError(`Failed to load PDF: ${errorMessage}`);
              }
            });
        } catch (error) {
          console.error('Error in loadPdfDocument:', error);
          retryBtn.style.display = 'block';
          setError(`Failed to initialize PDF: ${error instanceof Error ? error.message : String(error)}`);
        }
      };

      loadPdfDocument();
    };

    loadPdfJs().catch((err) => {
      console.error('Error in PDF.js initialization:', err);
      setError(`Failed to initialize PDF viewer: ${err instanceof Error ? err.message : String(err)}`);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      if (containerDiv) {
        containerDiv.innerHTML = '';
      }
    };
  }, [isWeb, pdfUri]);

  const getPdfViewerHtml = () => {
    const pdfSrc = pdfBase64 || pdfUri || '';
    return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0">
      <title>PDF.js Viewer</title>
      <style>
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
          background: #f8f9fa;
        }
        
        #viewerContainer {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 40px;
          left: 0;
          overflow: auto;
          background: #eee;
          -webkit-overflow-scrolling: touch;
        }
        
        #viewer {
          width: 100%;
        }
        
        .page {
          margin: 10px auto;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          background: white;
        }
        
        .progress-container {
          position: fixed;
          bottom: 50px;
          left: 10px;
          right: 10px;
          height: 10px;
          background: #e0e0e0;
          border-radius: 5px;
          overflow: hidden;
          z-index: 1000;
        }
        
        .progress-bar {
          height: 100%;
          background: #4285f4;
          width: 0;
          transition: width 0.3s;
        }
        
        .completed {
          background: #4CAF50;
        }
      </style>
    </head>
    <body>
      <div id="viewerContainer">
        <div id="viewer"></div>
      </div>
      <div class="progress-container">
        <div id="progress" class="progress-bar"></div>
      </div>
    
      <script src="https://cdn.jsdelivr.net/npm/pdfjs-dist@3.7.107/build/pdf.min.js"></script>
      <script>
        // Debugging
        console.log = function(message) {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'LOG', message: message }));
          }
        };
        
        console.error = function(message) {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'ERROR_LOG', message: message }));
          }
        };
      
        // PDF.js setup
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.7.107/build/pdf.worker.min.js';
        
        let pdfDoc = null;
        let pageNum = 1;
        let totalPages = 0;
        let currentScrollPosition = 0;
        let maxScrollPosition = 0;
        let isScrolledToBottom = false;
        let hasMarkedAsRead = false;
        const viewer = document.getElementById('viewer');
        const viewerContainer = document.getElementById('viewerContainer');
        const progressBar = document.getElementById('progress');
        
        // Get PDF URL from the component
        const pdfUrl = "${pdfSrc}";
        console.log('Loading PDF from URL:', pdfUrl);
        
        // Add fallback timer in case the PDF doesn't load or scroll events don't fire
        setTimeout(function() {
          if (!hasMarkedAsRead) {
            console.log('Marking as read after fallback timeout');
            markAsRead();
          }
        }, 30000); // 30 seconds timeout
        
        // Load the PDF
        pdfjsLib.getDocument(pdfUrl).promise.then(function(pdf) {
          pdfDoc = pdf;
          totalPages = pdf.numPages;
          
          // Render all pages one after another
          for (let i = 1; i <= totalPages; i++) {
            renderPage(i);
          }
          
        }).catch(function(error) {
          console.error('Error loading PDF:', error);
          
          // Notify React Native of the error
          try {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'ERROR',
              message: error.message
            }));
          } catch (e) {
            console.error('Failed to send error to React Native:', e);
          }
        });
        
        // Render a page of the PDF
        function renderPage(num) {
          pdfDoc.getPage(num).then(function(page) {
            const viewport = page.getViewport({ scale: 1.0 });
            const scale = viewerContainer.clientWidth / viewport.width;
            const scaledViewport = page.getViewport({ scale });
            
            const pageDiv = document.createElement('div');
            pageDiv.className = 'page';
            pageDiv.dataset.pageNumber = num;
            pageDiv.style.width = scaledViewport.width + 'px';
            pageDiv.style.height = scaledViewport.height + 'px';
            viewer.appendChild(pageDiv);
            
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = scaledViewport.height;
            canvas.width = scaledViewport.width;
            
            pageDiv.appendChild(canvas);
            
            const renderContext = {
              canvasContext: context,
              viewport: scaledViewport
            };
            
            page.render(renderContext).promise.then(function() {
              if (num === 1) {
                setupScrollHandling();
              }
              
              if (num === totalPages) {
                setTimeout(() => {
                  maxScrollPosition = viewerContainer.scrollHeight - viewerContainer.clientHeight;
                  console.log('Max scroll position:', maxScrollPosition);
                }, 500);
              }
            });
          });
        }
        
        function setupScrollHandling() {
          ['scroll', 'touchend', 'touchmove'].forEach(eventType => {
            viewerContainer.addEventListener(eventType, handleViewerScroll);
          });
        }
        
        function handleViewerScroll() {
          currentScrollPosition = viewerContainer.scrollTop;
          
          const pageElements = document.querySelectorAll('.page');
          let visiblePage = 1;
          
          pageElements.forEach(page => {
            const rect = page.getBoundingClientRect();
            if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
              visiblePage = parseInt(page.dataset.pageNumber);
            }
          });
          
          if (pageNum !== visiblePage) {
            pageNum = visiblePage;
          }
          
          if (maxScrollPosition > 0) {
            const progress = (currentScrollPosition / maxScrollPosition) * 100;
            progressBar.style.width = progress + '%';
            
            // Check if scrolled to bottom (within 50px or 90% through document)
            isScrolledToBottom = (currentScrollPosition >= maxScrollPosition - 50) || (progress >= 90);
            
            if (isScrolledToBottom && !hasMarkedAsRead) {
              markAsRead();
            }
          }
        }
        
        function markAsRead() {
          if (hasMarkedAsRead) return;
          
          hasMarkedAsRead = true;
          progressBar.classList.add('completed');
          progressBar.style.width = '100%';
          
          // Notify React Native that document has been read
          try {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'END_REACHED',
              reached: true
            }));
            console.log('Sent document viewed notification to React Native');
          } catch (e) {
            console.error('Failed to send notification to React Native:', e);
          }
        }
        
        // Add touch event listeners to detect user activity
        document.addEventListener('touchstart', function() {
          if (!hasMarkedAsRead) {
            console.log('User interaction detected');
          }
        });
      </script>
    </body>
    </html>
    `;
  };

  const AcceptButton = () => (
    <PressableStyled
      onPress={() => {
        // For testing:
        // setLoading(true);
        // dismiss();
        // dispatch(updateTermsAccepted(true));
        setLoading(true);
        acceptTerms().then(() => {
          setLoading(false);
          dismiss?.();
          dispatch(updateTermsAccepted(true));
          navigation.navigate('Home');
          console.log('Terms accepted');
        }).catch((err) => {
          console.error('Error accepting terms:', err);
          setError('Failed to accept terms. Please try again.');
        });
      }}
      disabled={!hasReachedEnd || !tickedAccepted}
      style={styles.endReachedIndicator}>
      <Text style={styles.endReachedText}>Accept & Continue</Text>
    </ PressableStyled>
  );

  const TickAccepted = () => (
    <PressableStyled
      onPress={() => setTickedAccepted(!tickedAccepted)}
      disabled={!hasReachedEnd}
      style={{
        marginVertical: 20,
        flexDirection: 'row',
        alignItems: 'center',
      }}>
      <View
        style={{
          width: 20,
          height: 20,
          borderRadius: 2,
          borderWidth: 2,
          borderColor: tickedAccepted
            ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlue100,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: tickedAccepted ? Colors.light.hereworksBlue500 : 'transparent',
        }}>
        {tickedAccepted && (
          <Text style={{ color: '#fff', fontSize: 16 }}>✓</Text>
        )}
      </View>
      <Text style={{ marginLeft: 10, fontSize: 16 }}>
        I have read and accept the terms and conditions
      </Text>
    </PressableStyled>
  );

  // Render different content based on platform and conditions
  if (!pdfUri || error) {
    return (
      <View style={styles.containerWeb}>
        {loading ? (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={Colors.light.hereworksBlue500} />
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        ) : (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error || 'Failed to load PDF'}</Text>
          </View>
        )}
      </View>
    );
  }

  if (isWeb) {
    return (
      <View style={styles.containerWeb}>
        <Text style={styles.heading}>
          Important: Review and Accept Updated Terms and Conditions
        </Text>
        <Text style={styles.description}>
          {'To continue using our services, you must read and accept the updated Terms and Conditions. These updates reflect our latest legal obligations and provide clearer details about your rights and responsibilities when using our platform.'}
        </Text>
        <Text style={styles.subheading}>
          Please scroll through and review the document below.
        </Text>
        <div
          ref={webDivRef}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            position: 'relative',
          }}
        />

        {loading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={Colors.light.hereworksBlue500} />
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        )}
        <TickAccepted />
        <AcceptButton />
      </View>
    );
  }

  // Mobile rendering with PDF.js viewer - this part remains unchanged
  return (
    <View style={styles.container}>
      <WebView
        ref={webViewRef}
        source={{
          html: getPdfViewerHtml(),
          baseUrl: '',
        }}
        style={styles.webview}
        originWhitelist={['*']}
        javaScriptEnabled={true}
        domStorageEnabled={true}
        allowFileAccess={true}
        allowUniversalAccessFromFileURLs={true}
        allowFileAccessFromFileURLs={true}
        mixedContentMode="always"
        onLoadStart={() => setLoading(true)}
        onLoad={() => setLoading(false)}
        onLoadEnd={() => setLoading(false)}
        onError={(syntheticEvent) => {
          const { nativeEvent } = syntheticEvent;
          console.error('WebView error:', nativeEvent);
          setError(`Failed to load PDF: ${nativeEvent.description}`);
        }}
        onMessage={(event) => {
          try {
            console.log('Message from WebView:', event.nativeEvent.data);
            const data = JSON.parse(event.nativeEvent.data);

            if (data.type === 'LOG') {
              console.log('WebView log:', data.message);
            } else if (data.type === 'ERROR_LOG') {
              console.error('WebView error log:', data.message);
            } else if (data.type === 'END_REACHED' && data.reached) {
              console.log('Received end reached message from WebView');
              setHasReachedEnd(true);
            } else if (data.type === 'ERROR') {
              setError(`PDF Error: ${data.message}`);
            }
          } catch (err) {
            console.error('Error parsing WebView message:', err);
          }
        }}
      />

      {loading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={Colors.light.hereworksBlue500} />
          <Text style={styles.loadingText}>Loading PDF...</Text>
        </View>
      )}
      <TickAccepted />
      <AcceptButton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  containerWeb: {
    flex: 1,
    backgroundColor: '#fff',
    width: '100%',
    maxWidth: 720,
    maxHeight: '80%',
    paddingHorizontal: 40,
    borderRadius: 20,
    paddingBottom: 16,
  },
  webview: {
    flex: 1,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: Colors.light.hereworksBlue500,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    color: 'red',
    fontSize: 16,
    textAlign: 'center',
  },
  endReachedIndicator: {
    backgroundColor: 'rgba(0, 128, 0, 0.9)',
    padding: 12,
    margin: 16,
    borderRadius: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  endReachedText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
  },
  heading: {
    fontSize: 28,
    lineHeight: 33,
    fontFamily: 'Poppins_600SemiBold',
    marginTop: 40,
    marginBottom: 16,
  },
  subheading: {
    fontSize: 14,
    fontFamily: 'Roboto_500Medium',
    marginBottom: 24,
    color: Colors.light.hereworksBlack300,
  },
  description: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    marginBottom: 24,
    color: Colors.light.hereworksBlack300,
  },
});
